/**
 * Has this browser ever signed in to Newsquawk?
 *
 * Written once on any successful auth and NEVER removed — deliberately unlike
 * `nq:last-auth-user`, which sits beside it and is cleared on logout and on a
 * dead-session redirect. That one answers "who is signed in"; this answers
 * "has anyone ever been", which is a different question with a different
 * lifetime.
 *
 * It exists so the guided demo stops asking an existing customer what they
 * trade. Someone who has an account and comes back to log in is, to every
 * other check in the app, simply logged out — so they got the full-screen
 * "What do you trade?" chooser in front of the login button, every visit.
 *
 * Deliberately NOT identifying. `nq:last-auth-user` holds an id, username and
 * email, and reading that to decide whether to render a modal would mean
 * handling PII for a presentation decision. A bare "1" answers the only
 * question being asked.
 *
 * Per-browser and best-effort: a new device, a different browser or cleared
 * storage and the visitor looks new again. That is an acceptable floor for
 * suppressing a prompt — the cost of getting it wrong is one modal, not a
 * broken session — and it is why the demo still needs its own route to a
 * trial for anyone this misses.
 */

const PRIOR_AUTH_KEY = "nq:has-authed";

/** Call on any successful authentication. Idempotent. */
export function markPriorAuth(): void {
  try {
    localStorage.setItem(PRIOR_AUTH_KEY, "1");
  } catch {
    // localStorage may be unavailable in some corporate environments — the
    // demo showing again is a far smaller problem than a thrown error here.
  }
}

/**
 * True only for a browser that has signed in at some point.
 *
 * Returns false when storage is unreadable, which keeps the demo working for
 * a genuine first-time visitor in a locked-down environment. The failure mode
 * is showing the demo to someone who has seen it, never hiding the product
 * from someone who has not.
 */
export function hasPriorAuth(): boolean {
  try {
    return localStorage.getItem(PRIOR_AUTH_KEY) === "1";
  } catch {
    return false;
  }
}
